import {Component, OnInit} from '@angular/core';
import {
  ApplicationDialogService,
  BusinessSearchFn, PortalHotToastService,
  setupUntilDestroy,
  getUser,
  BankStatementOverviewComponent,
} from '@portal-workspace/grow-ui-library';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {UntilDestroy} from '@ngneat/until-destroy';
import {ApplicationService} from '../../service/application.service';
import { of, Subscription, combineLatest } from 'rxjs';
import {
  BankStatementData,
  BankstatementAndBasiqDataStatus,
  BasiqUserCustomerMapping,
  BusinessSearchValue,
  PayloadApiResponse,
  User,
  encodeReferenceForBankstatement,
  isAdmin,
  isAnalyst,
  isInternalUser
} from '@portal-workspace/grow-shared-library';
import { CdkStepper, CdkStepperModule } from '@angular/cdk/stepper';
import { CustomerAnalysisComponent } from '@portal-workspace/grow-ui-library';
import { AccountSummaryComponent } from '@portal-workspace/grow-ui-library';
import { MarkDirective } from '@portal-workspace/grow-ui-library';
import { BusinessSearchComponent } from '@portal-workspace/grow-ui-library';
import { NgTemplateOutlet } from '@angular/common';
import { ApplicationStepper2Component,setStepper2StepConfig } from '@portal-workspace/grow-ui-library';
import {Response} from 'request';
import { MatButtonModule } from '@angular/material/button';
import {WhitelabelService} from "../../service/whitelabel.service";
import { catchError, switchMap, tap } from "rxjs/operators";
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import {PortalThemeService} from "../../service/portal-theme.service";

const DEFAULT_LOGO = 'https://growpublicaccess.blob.core.windows.net/logos/SVG/DYNA_Logo_103023_Master-Pos.svg'
const DEFAULT_LOGO_DARK_MODE = 'https://growpublicaccess.blob.core.windows.net/logos/SVG/DYNA_Logo_103023_Master-Rev.svg';

@UntilDestroy({arrayName: 'subscriptions'})
@Component({
    templateUrl: './bank-statements.page.html',
    styleUrls: ['./bank-statements.page.scss'],
    standalone: true,
  imports: [
    ApplicationStepper2Component,
    CdkStepperModule,
    MatButtonModule,
    NgTemplateOutlet,
    FormsModule,
    ReactiveFormsModule,
    BusinessSearchComponent,
    MarkDirective,
    AccountSummaryComponent,
    CustomerAnalysisComponent,
    BankStatementOverviewComponent,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatSelectModule
  ]
})
export class BankStatementsPage implements OnInit {
  businessSearchPlaceholder: string = "Enter name or ABN…";

  logo = DEFAULT_LOGO;

  isAdmin = isAdmin;
  isAnalyst = isAnalyst;
  isInternalUser = isInternalUser;
  loggedInUser: User | null = getUser();

  subscriptions: Subscription[] = [];

  hasBankStatement: boolean = false;
  hasBasiqStatement: boolean = false;

  // step 1:
  step1SearchFn!: BusinessSearchFn;
  formGroupStep1!: FormGroup<{
    business: FormControl<BusinessSearchValue>;
  }>;
  formControlStep1Business!: FormControl<BusinessSearchValue>;

  // step 2
  formGroupStep2!: FormGroup<{}>;
  data!: BankStatementData;
  selectedStatementSource: 'illion' | 'basiq' = 'illion';

  constructor(
    private formBuilder: FormBuilder,
    private applicationService: ApplicationService,
    private applicationDialogService: ApplicationDialogService,
    private toastService: PortalHotToastService,
    private whitelabelService: WhitelabelService,
    private portalThemeService: PortalThemeService,
  ) {}

  ngOnInit() {
    setupUntilDestroy(this);

    this.subscriptions.push(combineLatest([
      this.whitelabelService.subject.asObservable(),
      this.portalThemeService.subject.asObservable(),
    ]).pipe(
      tap(([whitelabelEvent, themeEvent])=>{
        if (whitelabelEvent && whitelabelEvent.payload.logoLink) {
          this.logo = whitelabelEvent.payload.logoLink;
        } else {
          if (themeEvent && themeEvent.portalTheme == 'dark') {
            this.logo = DEFAULT_LOGO_DARK_MODE;
          } else {
            this.logo = DEFAULT_LOGO;
          }
        }
      })
    ).subscribe());

    this.initStep1();
    this.initStep2();
  }

  get abn() {
    return this.formControlStep1Business.value?.abn;
  }

  get businessName() {
    const businessSearchValue  = this.formControlStep1Business.value;
    if (businessSearchValue && businessSearchValue.type === 'search-result') {
        return businessSearchValue.result?.organisationName ?? businessSearchValue.organisationName;
    }
    return businessSearchValue?.organisationName;
  }

  initStep1() {

    this.formControlStep1Business = this.formBuilder.control(null, [Validators.required]);
    this.formGroupStep1 = this.formBuilder.group({
      business: this.formControlStep1Business,
    });
    this.step1SearchFn = this.applicationService.businessSearchFn;

    setStepper2StepConfig(this.formGroupStep1, {
      showPreviousStepButton:false,
      nextStepClickedFn: async stepper => {
        if (this.formControlStep1Business.valid) {
          this.loadBSAData(stepper);
        }
      },
    });
  }

  loadBSAData(stepper: CdkStepper) {
    this.subscriptions.push(this.applicationService.getBankStatementAndBasiqDataStatusFn(encodeReferenceForBankstatement(this.abn!, 'Business')!, 'Business')
      .pipe(this.toastService.spinnerObservable())
      .pipe(
        tap((r: PayloadApiResponse<BankstatementAndBasiqDataStatus | null>) => {
          console.log('result', r)
          if (r.status && r.payload) {
            this.hasBankStatement = r.payload.hasBankStatement;
            this.hasBasiqStatement = r.payload.hasBasiqStatement;
            if (this.hasBankStatement) {
              this.selectedStatementSource = 'illion';
              this.loadIllionData(stepper);
            } else if (this.hasBasiqStatement) {
              this.selectedStatementSource = 'basiq';
              this.loadBasiqData(stepper);
            }
          }
          else {
            this.hasBankStatement = false;
            this.hasBasiqStatement = false;
          }
        })
      )
      .subscribe())
  }

  loadIllionData(stepper?: CdkStepper) {
    this.subscriptions.push(
      this.applicationService.getBankStatementsAnalysis({ reference: this.abn as string })
        .pipe(this.toastService.spinnerObservable())
        .subscribe(
          (result: PayloadApiResponse<BankStatementData | null>) => {
            if (result.status && result.payload) {
              this.data = result.payload;
              if (!Object.keys(this.data).length) {
                this.applicationDialogService.openAlertDialog({
                  message: `Alert`,
                  subMessage: `We don't have data for this business.`
                });
              }
              else if (stepper) {
                stepper.next();
              }
            }
            else {
              this.applicationDialogService.openAlertDialog({
                message: `Error`,
                subMessage: result.message
              })
            }
          })

    );
  }

  onStatementSourceChange() {
    if (this.selectedStatementSource === 'basiq') {
      this.loadBasiqData();
    } else {
      this.loadIllionData();
    }
  }

  loadBasiqData(stepper?: CdkStepper) {
    this.subscriptions.push(
      this.applicationService.getBasiqCustomerMappingByAbnFn(this.abn!)
        .pipe(
          this.toastService.spinnerObservable(),
          switchMap((result: PayloadApiResponse<BasiqUserCustomerMapping | null>) => {
            if (result.status && result.payload) {
              const companyMapping = result.payload;
              return this.applicationService.getBasiqStatementDataForCompany(companyMapping)
                .pipe(this.toastService.spinnerObservable());
            } else {
              // Handle the case where the first call doesn't return valid data
              console.error('Invalid customer mapping result');
              return of(null); // or throw an error, or return a default value
            }
          }),
          catchError(error => {
            console.error('Error in combined subscription:', error);
            return of(null); // or handle the error as needed
          })
        )
        .subscribe(
          (statementData) => {
            if (statementData?.status && statementData?.payload) {
              this.data = statementData.payload;
              if (stepper) {
                stepper.next();
              }
            }
          }
        )
    );
  }

  initStep2() {
    this.formGroupStep2 = this.formBuilder.group({});

    setStepper2StepConfig(this.formGroupStep2, {
      nextStepClickedFn: async stepper => {
        stepper.previous();
      },
      nextStepButtonText:"Finish"
    });
  }

  onClickBack(stepper: CdkStepper) {
    if (stepper.selectedIndex) {
      this.formControlStep1Business.reset();
      stepper.previous();
    }
  }

  refreshBankStatement() {
    this.subscriptions.push(
      this.applicationService.refreshBankStatement(this.abn as string).pipe(
        this.toastService.spinnerObservable()
      ).subscribe(
        (result: PayloadApiResponse<Response | {Error: string}>) => {
          console.log('refresh result: ', result)
          if (result.status) {
            this.applicationDialogService.successDialog({
              message: 'Success',
              subMessage: 'The bank statement has been refreshed'
            }).afterClosed().subscribe(() => {
              this.applicationService.getBankStatementsAnalysis({ reference: this.abn as string})
              .pipe(this.toastService.spinnerObservable())
              .subscribe(
                (result: PayloadApiResponse<BankStatementData | null>) => {
                  if (result.status && result.payload) {
                    this.data = result.payload;
                    console.log(result);
                    if (!Object.keys(this.data).length) {
                      this.applicationDialogService.openAlertDialog({
                        message: `Alert`,
                        subMessage: `We don't have data for this business.`
                      });
                      }
                    }
                    else {
                      this.applicationDialogService.openAlertDialog({
                        message: `Error`,
                        subMessage: result.message
                      })
                    }
                })
            })
          } else {
            this.applicationDialogService.openAlertDialog({
              message: 'Error',
              subMessage: 'Unable to refresh this bank statement'
            })
          }
        }
      )
    )
  }
}
